import { WorkspaceTypeID } from '../../lib/api/workspaces/dto';
import { FeatureFlag } from '../useFeatureFlag';
import type { PermissionSubject } from '../usePermissions';

const getDashboardPrefix = (workspaceTypeID: WorkspaceTypeID): string => {
  switch (workspaceTypeID) {
    case WorkspaceTypeID.HCPExpansion:
      return 'studio_hcp_audiences_extension';
    case WorkspaceTypeID.Throtle:
      return 'throtle_workspace';
    case WorkspaceTypeID.DTC:
      return 'studio_dtc_audiences';
    case WorkspaceTypeID.HCPSiteAnalytics:
      return 'hcp365_v2_site_view_app';
    default:
      return 'studio_hcp_audiences';
  }
};

const getBaseSuffixes = (workspaceTypeID: WorkspaceTypeID): string[] => {
  switch (workspaceTypeID) {
    case WorkspaceTypeID.HCPExpansion:
      return ['studio_hcp_audiences_extension_header'];
    case WorkspaceTypeID.Throtle:
      return ['throtle_attributes'];
    case WorkspaceTypeID.DTC:
      return [
        'studio_dtc_audiences_clinical',
        'studio_dtc_audiences_npis',
        'studio_dtc_audiences_patients',
        'studio_dtc_audiences_top_line_metrics',
        'studio_dtc_audiences_filters',
      ];
    case WorkspaceTypeID.HCPSiteAnalytics:
      return ['hcp365_site_view'];
    default:
      return ['studio_hcp_audiences_header'];
  }
};

type HasPermission = (permissionSubject: PermissionSubject) => boolean;
type HasFeatureFlag = (flag: FeatureFlag) => boolean;
type GuardFn = (hasPermission: HasPermission, hasFeatureFlag?: HasFeatureFlag) => boolean;

type DashboardSectionsMapping = Record<string, PermissionSubject[] | GuardFn>;

const getProtectedSuffixes = (workspaceTypeID: WorkspaceTypeID): DashboardSectionsMapping => {
  switch (workspaceTypeID) {
    case WorkspaceTypeID.HCPExpansion:
      return {
        studio_hcp_audiences_extension_claims: ['Clinical'],
        studio_hcp_audiences_extension_moments: ['Moments'],
        studio_hcp_audiences_extension_hcp365: ['ChannelsOperational'],
        studio_hcp_audiences_extension_npi_details_title: (hasPermission) =>
          (['NPIDetails', 'NPIProfessions'] as const).some(hasPermission),
        studio_hcp_audiences_extension_npi_details: ['NPIDetails'],
        studio_hcp_audiences_extension_profession: ['NPIProfessions'],

        studio_hcp_audiences_extension_patient_details_pfron: (hasPermission, hasFeatureFlag) =>
          hasPermission('PatientDetails') && !hasFeatureFlag?.(FeatureFlag.HCPNewInsuranceVisType),
        studio_hcp_audiences_extension_patient_details: (hasPermission, hasFeatureFlag) =>
          hasPermission('PatientDetails') && !!hasFeatureFlag?.(FeatureFlag.HCPNewInsuranceVisType),
      };
    case WorkspaceTypeID.Throtle:
      return {};
    case WorkspaceTypeID.DTC:
      return {};
    case WorkspaceTypeID.HCPSiteAnalytics:
      return {};
    default:
      return {
        studio_hcp_audiences_claims: ['Clinical'],
        studio_hcp_audiences_moments: ['Moments'],
        studio_hcp_audiences_hcp365: ['ChannelsOperational'],
        studio_hcp_audiences_npi_details_title: (hasPermission) =>
          (['NPIDetails', 'NPIProfessions'] as const).some(hasPermission),
        studio_hcp_audiences_npi_details: ['NPIDetails'],
        studio_hcp_audiences_profession: ['NPIProfessions'],

        studio_hcp_audiences_patient_details_pfron: (hasPermission, hasFeatureFlag) =>
          hasPermission('PatientDetails') && !hasFeatureFlag?.(FeatureFlag.HCPNewInsuranceVisType),
        studio_hcp_audiences_patient_details: (hasPermission, hasFeatureFlag) =>
          hasPermission('PatientDetails') && !!hasFeatureFlag?.(FeatureFlag.HCPNewInsuranceVisType),
      };
  }
};

export const getDashboardBaseSections = (workspaceTypeID: WorkspaceTypeID) => {
  const prefix = getDashboardPrefix(workspaceTypeID);
  const suffixes = getBaseSuffixes(workspaceTypeID);

  return suffixes.reduce((acc, suffix) => {
    acc.push(`${prefix}::${suffix}`);
    return acc;
  }, [] as string[]);
};

export const getDashboardProtectedSections = (workspaceTypeID: WorkspaceTypeID) => {
  const prefix = getDashboardPrefix(workspaceTypeID);

  return Object.entries(getProtectedSuffixes(workspaceTypeID)).reduce((acc, [key, value]) => {
    acc[`${prefix}::${key}`] = value;
    return acc;
  }, {} as DashboardSectionsMapping);
};
